import * as React from 'react';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import ListItemText from '@mui/material/ListItemText';
import Select from '@mui/material/Select';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function MultipleSelectCheckmarks(props) {
  const {
    data = [],
    labelProp = 'name',
    label = 'Wybierz pojazdy',
    value = [],
    onChange,
    customRender,
    width = 500,
    showSelectedList = true,
    selectedListTitle = null,
    allowRemoveFromList = true,
    ...otherProps
  } = props;

  const [selectedIds, setSelectedIds] = React.useState(value);

  React.useEffect(() => {
    setSelectedIds(value);
  }, [value]);

  const handleChange = (event) => {
    const {
      target: { value: newValue },
    } = event;

    // newValue contains the selected _id values
    const updatedIds = typeof newValue === 'string' ? newValue.split(',') : newValue;
    setSelectedIds(updatedIds);

    if (onChange) {
      onChange(updatedIds);
    }
  };

  const handleRemoveItem = (itemId) => {
    const updatedIds = selectedIds.filter((id) => id !== itemId);
    setSelectedIds(updatedIds);

    if (onChange) {
      onChange(updatedIds);
    }
  };

  const getSelectedLabels = (selectedIds) => {
    return selectedIds
      .map((id) => {
        const item = data.find((item) => item._id === id);
        if (!item) return '';

        if (customRender) {
          return customRender(item);
        }

        return item[labelProp] || item._id;
      })
      .filter(Boolean);
  };

  const getItemById = (id) => data.find((item) => item._id === id);

  const renderSelectedItem = (itemId) => {
    const item = getItemById(itemId);
    if (!item) return null;

    if (customRender) {
      return customRender(item);
    }

    return item[labelProp] || item._id;
  };

  const renderSelectedItemsList = () => {
    if (!showSelectedList) return null;

    if (selectedIds.length === 0) {
      return (
        <Box sx={{ mt: 2 }}>
          <Typography variant='h6'>{selectedListTitle}</Typography>
          <Typography variant='body2' color='text.secondary'>
            Brak wybranych pojazdów
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ mt: 2 }}>
        <Typography variant='h6'>{selectedListTitle}</Typography>
        <List dense>
          {selectedIds.map((itemId) => (
            <ListItem
              key={itemId}
              secondaryAction={
                allowRemoveFromList && (
                  <IconButton edge='end' aria-label='delete' onClick={() => handleRemoveItem(itemId)}>
                    <DeleteIcon />
                  </IconButton>
                )
              }
            >
              <ListItemText primary={renderSelectedItem(itemId)} />
            </ListItem>
          ))}
        </List>
      </Box>
    );
  };

  return (
    <Box>
      <FormControl sx={{ m: 1, width }} {...otherProps}>
        <InputLabel id='multiple-checkbox-label'>{label}</InputLabel>
        <Select
          labelId='multiple-checkbox-label'
          id='multiple-checkbox'
          multiple
          value={selectedIds}
          onChange={handleChange}
          input={<OutlinedInput label={label} />}
          renderValue={(selected) => getSelectedLabels(selected).join(', ')}
          MenuProps={MenuProps}
        >
          {data.map((item) => {
            const itemLabel = customRender ? customRender(item) : item[labelProp] || item._id;
            return (
              <MenuItem key={item._id} value={item._id}>
                <Checkbox checked={selectedIds.includes(item._id)} />
                <ListItemText primary={itemLabel} />
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {renderSelectedItemsList()}
    </Box>
  );
}
